import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AdminUserMap = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [locationData, setLocationData] = useState([]);
  const [stats, setStats] = useState({
    total_users: 0,
    users_with_location: 0,
    users_by_state: {}
  });

  useEffect(() => {
    const isAdmin = localStorage.getItem('shootzu_admin');
    if (!isAdmin) navigate('/szadmin');
  }, [navigate]);

  useEffect(() => {
    fetchLocationData();
  }, []);

  useEffect(() => {
    if (locationData.length > 0 && !googleMapRef.current) {
      loadGoogleMapsScript();
    }
  }, [locationData]);

  const fetchLocationData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/location-data`
      );
      
      setLocationData(response.data.location_data || []);
      setStats(response.data.stats || {});
      
      if (response.data.location_data.length === 0) {
        toast.info('No user location data available for mapping');
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch location data:', error);
      toast.error('Failed to load user location data');
      setLoading(false);
    }
  };

  const loadGoogleMapsScript = async () => {
    try {
      // Fetch Google Maps API key from admin settings (requires admin auth)
      const token = localStorage.getItem('shootzu_admin_token');
      const settingsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const apiKey = settingsResponse.data?.googleMaps?.apiKey;
      
      if (!apiKey) {
        toast.error('Google Maps API key not configured in admin settings');
        setLoading(false);
        return;
      }

      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        initializeMap();
        return;
      }

      // Load Google Maps core (HeatmapLayer/visualization was removed in Maps JS v3.65).
      // NOTE: no loading=async here — we rely on script.onload guaranteeing the API
      // is ready; loading=async defers init and would make google.maps.Map undefined onload.
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => initializeMap();
      script.onerror = () => {
        toast.error('Failed to load Google Maps');
        setLoading(false);
      };
      document.head.appendChild(script);
    } catch (error) {
      console.error('Error loading Google Maps:', error);
      toast.error('Failed to configure Google Maps');
      setLoading(false);
    }
  };

  const initializeMap = () => {
    if (!mapRef.current || !window.google?.maps?.Map) {
      setLoading(false);
      return;
    }

    try {
      // Center on Australia
      const australiaCenter = { lat: -25.2744, lng: 133.7751 };
      
      // Initialize map
      const map = new window.google.maps.Map(mapRef.current, {
        zoom: 5,
        center: australiaCenter,
        mapTypeId: 'roadmap',
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });
      
      googleMapRef.current = map;

      // Render a density / heat-style map with translucent Circle overlays.
      // (Google removed visualization.HeatmapLayer in Maps JS v3.65, and the
      // classic Marker is deprecated — Circles are core, supported, and dep-free.)
      if (locationData.length > 0) {
        // Aggregate points into ~0.1° grid cells (~11km) to gauge density
        const cells = {};
        locationData.forEach(point => {
          const key = `${point.lat.toFixed(1)},${point.lng.toFixed(1)}`;
          if (!cells[key]) cells[key] = { latSum: 0, lngSum: 0, count: 0, state: point.state };
          cells[key].latSum += point.lat;
          cells[key].lngSum += point.lng;
          cells[key].count += 1;
        });

        const cellList = Object.values(cells);
        const maxCount = Math.max(...cellList.map(c => c.count), 1);

        // Blue (low) → red (high) intensity ramp
        const heatColor = (t) => {
          const stops = [
            [0.0, [0, 102, 255]],
            [0.35, [0, 224, 224]],
            [0.6, [124, 224, 0]],
            [0.8, [255, 196, 0]],
            [1.0, [255, 48, 48]],
          ];
          let lo = stops[0], hi = stops[stops.length - 1];
          for (let i = 0; i < stops.length - 1; i++) {
            if (t >= stops[i][0] && t <= stops[i + 1][0]) { lo = stops[i]; hi = stops[i + 1]; break; }
          }
          const f = (t - lo[0]) / ((hi[0] - lo[0]) || 1);
          const ch = (i) => Math.round(lo[1][i] + (hi[1][i] - lo[1][i]) * f);
          return `rgb(${ch(0)}, ${ch(1)}, ${ch(2)})`;
        };

        cellList.forEach(cell => {
          const lat = cell.latSum / cell.count;
          const lng = cell.lngSum / cell.count;
          const intensity = cell.count / maxCount;
          const color = heatColor(intensity);

          // Soft density halo — larger & warmer where more users cluster
          new window.google.maps.Circle({
            map,
            center: { lat, lng },
            radius: 25000 + intensity * 90000, // metres
            fillColor: color,
            fillOpacity: 0.28,
            strokeColor: color,
            strokeOpacity: 0.5,
            strokeWeight: 1,
            clickable: false,
          });

          // Solid core dot so even single-user locations stay visible
          new window.google.maps.Circle({
            map,
            center: { lat, lng },
            radius: 6000,
            fillColor: color,
            fillOpacity: 0.95,
            strokeWeight: 0,
            clickable: false,
          });
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error initializing map:', error);
      toast.error('Failed to initialize map');
      setLoading(false);
    }
  };

  const stateData = Object.entries(stats.users_by_state || {})
    .map(([state, count]) => ({ state, count }))
    .sort((a, b) => b.count - a.count);

  const mappedTotal = stats.users_with_location || 0;
  const pct = (count) =>
    mappedTotal > 0 ? Math.round((count / mappedTotal) * 100) : 0;

  return (
    <div>
      {/* Sticky Page Header */}
      <header className="sz-header flex items-center justify-between gap-[18px]">
        <div>
          <h1 className="sz-h1 m-0 whitespace-nowrap">User Map</h1>
          <div className="mt-[3px] text-[12.5px] text-[#8A90A2]">
            Geographic distribution of crew &amp; businesses
          </div>
        </div>
      </header>

      <main style={{ padding: "22px 30px 56px", maxWidth: 1320 }}>
        {/* Stat cards */}
        <div
          className="mb-[18px] grid gap-[13px]"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
        >
          <div className="sz-card p-4">
            <div className="text-[12px] font-semibold text-[#8A90A2]">Total Users</div>
            <div className="sz-stat-num mt-2">
              {(stats.total_users || 0).toLocaleString()}
            </div>
          </div>
          <div className="sz-card p-4">
            <div className="text-[12px] font-semibold text-[#8A90A2]">Mapped Users</div>
            <div className="sz-stat-num mt-2">
              {(stats.users_with_location || 0).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Map row — stacks on mobile, side-by-side on lg */}
        <div className="flex flex-col gap-[18px] lg:flex-row">
          {/* Map panel — keeps the real Google Maps heatmap container */}
          <div
            className="relative min-w-0 flex-1 overflow-hidden border border-[#E6E9EF]"
            style={{ borderRadius: 18, height: 520 }}
          >
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0C1120]/80 backdrop-blur-sm">
                <div className="text-center">
                  <Loader2 className="mx-auto mb-3 h-10 w-10 animate-spin text-[#28C0D0]" />
                  <p className="text-sm text-white/70">Loading map…</p>
                </div>
              </div>
            )}
            <div ref={mapRef} className="h-full w-full" style={{ minHeight: 520 }} />

            {/* Low → High heat legend overlay */}
            <div
              className="pointer-events-none absolute z-[5] flex items-center gap-[10px] border border-white/10 px-[13px] py-2"
              style={{
                left: 16,
                bottom: 16,
                background: "rgba(12,17,32,.6)",
                backdropFilter: "blur(4px)",
                borderRadius: 11,
              }}
            >
              <span className="text-[11px] font-semibold text-white/70">Low</span>
              <div
                className="h-2 w-[120px] rounded-full"
                style={{ background: "linear-gradient(90deg,#28C0D0,#F59E0B,#FB7185)" }}
              />
              <span className="text-[11px] font-semibold text-white/70">High</span>
            </div>
          </div>

          {/* State Distribution sidebar */}
          <div className="sz-card w-full p-5 lg:w-[300px] lg:flex-none">

            <h3 className="sz-h3 mb-1 mt-0">State Distribution</h3>
            <div className="mb-4 text-[12px] text-[#8A90A2]">Share of mapped users</div>

            {stateData.length > 0 ? (
              <div className="flex flex-col gap-[14px]">
                {stateData.map((item, idx) => (
                  <div key={idx}>
                    <div className="mb-[5px] flex items-center justify-between">
                      <span className="text-[13px] font-semibold">{item.state}</span>
                      <span className="text-[12px] text-[#8A90A2]">
                        <strong className="text-[#0C1120]" style={{ fontFamily: "Poppins" }}>
                          {item.count.toLocaleString()}
                        </strong>{" "}
                        · {pct(item.count)}%
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-[#F1F3F7]">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${pct(item.count)}%`,
                          background: "linear-gradient(90deg,#572B7C,#78287C)",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-[#A8AEBC]">
                <MapPin className="mx-auto mb-2 h-10 w-10 opacity-30" />
                <p className="text-sm">No location data available</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminUserMap;
